import { memo, MouseEvent } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import { BubbleChatIcon } from "@hugeicons-pro/core-stroke-rounded";
import { cx } from "shared/utils";
import { useMessagePopupStore } from "shared/app/MessagePopup/store";
import { ChatBadge } from "shared/app/MessagePopup/components/ChatBadge";
import { useUnreadByContact } from "views/inbox/api/use-unread-by-contact";
import { Lead } from "views/leads-id/types";

interface MessageCellProps {
  lead: Lead;
}

/**
 * Row action that opens the floating message pop-up for the lead's contact
 * (CRM-1133), so staff can send a message without opening the record.
 *
 * Deliberately not disabled for a lead with no application: the pop-up itself
 * disables the Chat chip and explains why ("To use chat, the contact must have
 * at least one application"), while Email and SMS stay available.
 */
export const MessageCell = memo(function MessageCell({
  lead
}: MessageCellProps) {
  const openMessagePopup = useMessagePopupStore((state) => state.open);
  const contactId = lead.contact?.id;
  const unreadByContact = useUnreadByContact(Boolean(contactId));
  const unreads = contactId ? (unreadByContact.get(contactId) ?? 0) : 0;

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!contactId) return;
    openMessagePopup({
      contactId,
      leadId: lead.id,
      applicationId: lead.application_id || undefined,
      applicantId: lead.applicant_id || undefined,
      contactName: `${lead.first_name} ${lead.last_name}`.trim()
    });
  };

  return (
    // !overflow-visible is load-bearing: ag-theme-boom gives every direct child of
    // .ag-cell `overflow: hidden` for text truncation. The 52px column leaves 36px
    // inside the cell padding, and the 32px button plus the badge's 6px overhang
    // needs 38px, so without it the unread counter is clipped on the right
    // (CRM-1328). The badge still stays inside the 52px cell box itself.
    <div className="flex items-center justify-center w-full h-full !overflow-visible">
      <button
        type="button"
        aria-label="Message lead"
        disabled={!contactId}
        onClick={handleClick}
        className={cx(
          "relative flex items-center justify-center w-8 h-8 rounded-md transition-colors",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500",
          contactId
            ? "text-content-300 hover:text-content-500"
            : "text-content-200 cursor-not-allowed"
        )}
      >
        <HugeiconsIcon
          icon={BubbleChatIcon}
          size={20}
          className="shrink-0"
          strokeWidth={1.5}
        />
        <ChatBadge count={unreads} className="pointer-events-none" />
      </button>
    </div>
  );
});
